import React, {
    useCallback,
    useEffect,
    useLayoutEffect,
    useState,
} from 'react';
import Draggable from 'react-draggable';
import { connect } from 'react-redux';

import cn from 'classnames';
import BodyPortal from 'components/BodyPortal';

import appActionCreators from '../../app/appActionCreators';

import CloseButton from '../closeButton/closeButton';
import Overlay from '../overlay/overlay';

// styles
import css from './popup.css';

export type Props = {
    active?: boolean;
    inPortal?: boolean;
    hiddenCloseButton?: boolean;
    isCloseByButtonOnly?: boolean;
    fullContent?: boolean;
    rounded?: boolean;
    greyColor?: boolean;
    lowered?: boolean;
    centered?: boolean;
    subtitle?: React.ReactNode;
    title?: React.ReactNode;
    onClose: () => void;
    dataQa?: string;
    children: React.ReactNode;
    className?: string;
    withoutPadding?: boolean;
    overlayClassName?: string;
    isDraggable?: boolean;
};

type DispatchProps = {
    handleOpen: () => void;
    handleClose: () => void;
};

const Popup: React.FC<React.PropsWithChildren<Props & DispatchProps>> = (
    props,
) => {
    const {
        active,
        onClose,
        fullContent,
        greyColor,
        lowered,
        isCloseByButtonOnly,
        dataQa,
        hiddenCloseButton,
        title,
        subtitle,
        children,
        rounded,
        centered,
        inPortal,
        withoutPadding,
        isDraggable,
    } = props;
    const [activeApplied, setActiveApplied] = useState(false);

    const [mounted, setMounted] = useState(false);

    useEffect(() => {
        if (active && mounted) {
            const timer = requestAnimationFrame(() => setActiveApplied(true));

            return () => {
                cancelAnimationFrame(timer);
            };
        }

        return () => undefined;
    }, [active, mounted]);

    useEffect(() => {
        if (!active) {
            document.body.style.overflow = '';
            return () => undefined;
        } else {
            // when becomes not active, reset activeApplied
            return () => {
                setActiveApplied(false);
            };
        }
    }, [active]);

    useEffect(() => {
        setMounted(true);

        return () => {
            // todo: this has bad global effect [reset overflow state]
            document.body.style.overflow = '';
        };
    }, []);

    const handleClose = useCallback(() => {
        onClose();
        document.body.style.overflow = '';
    }, [onClose]);

    useLayoutEffect(() => {
        // may be unmounted externally (active would never be false)
        if (active) {
            props.handleOpen();
            return props.handleClose;
        } else {
            return () => undefined;
        }
    }, [active]);

    if (!active) {
        return null;
    }

    const totalClasses = cn(css.root, props.className, {
        [css.active]: activeApplied,
        [css.fullContent]: fullContent,
        [css.rootGrey]: greyColor,
        [css.rootLowered]: lowered,
        [css.rootCentered]: centered,
        [css.rounded]: rounded,
        [css.withoutPadding]: withoutPadding,
    });

    const content = () => {
        return (
            <div
                className={totalClasses}
                data-qa={dataQa ? `qa_popup ${dataQa}` : 'qa_popup'}
            >
                {!hiddenCloseButton && (
                    <div className={css.greyContent}>
                        <CloseButton withHover onClose={handleClose} />
                    </div>
                )}
                <div>
                    {(title || subtitle) && (
                        <div className={css.header}>
                            {title && <h3 className={css.title}>{title}</h3>}
                            {subtitle && (
                                <div className={css.subtitle}>{subtitle}</div>
                            )}
                        </div>
                    )}
                    {children}
                </div>
            </div>
        );
    };

    const nodes = (
        <Overlay
            active={active}
            isCloseByButtonOnly={isCloseByButtonOnly}
            onClose={handleClose}
            className={props.overlayClassName}
        >
            {isDraggable ? (
                <Draggable>
                    <span className={css.draggableWrapper}> {content()} </span>
                </Draggable>
            ) : (
                content()
            )}
        </Overlay>
    );

    if (inPortal) {
        return <BodyPortal>{nodes}</BodyPortal>;
    }

    return nodes;
};

const mapDispatchToProps = {
    handleOpen: appActionCreators.onOpenPopup,
    handleClose: appActionCreators.onClosePopup,
};

export default connect<Record<string, unknown> | null, DispatchProps, Props>(
    null,
    mapDispatchToProps,
)(Popup);
